import React from 'react';
import {useSelector} from 'react-redux';
import {Box, Button, Typography} from '@mui/material';
import ApiUtils from '../utils/apiUtils';
import {useUserSocket} from '../hooks/useUserSocket';
import ConfirmModal from '../pieces/modals/confirmPopup';
import AiCallIncomingPrompt from './AiCallIncomingPrompt';
import AiCallInspectionSearchDialog from './AiCallInspectionSearchDialog';
import {AI_CALL_INSPECTION_PROMPT_BODY} from '../utils/aiCallSession';
import {COLORS} from '../styles/styleConstants';

/**
 * Keeps a Socket.IO connection for this browser tab when `user.acceptAiCall` is true.
 * Auth includes `browserSessionId` so the server can keep one socket per tab for the same user.
 */
const UserSocketRegistration = () => {
  const user = useSelector((state) => state.user);

  const enabled =
    !ApiUtils.isMockMode() &&
    user.isAuthenticated &&
    Boolean(user.id) &&
    Boolean(user.acceptAiCall);

  const {aiCallInspectionPrompt, aiCallIncomingPrompt, aiCallInspectionSearch} = useUserSocket({
    enabled,
    userId: user.id || undefined,
    acceptAiCall: user.acceptAiCall,
    onDataReady: (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('socket data-ready', data);
      }
    },
  });

  return (
    <>
      <ConfirmModal
        open={aiCallInspectionPrompt.open}
        handleClose={aiCallInspectionPrompt.onClose}
        hideHeader
        width={{sx: '98%', md: '760px'}}
      >
        <Box textAlign="center" px={{xs: 1, md: 2}}>
          <Typography
            variant="body1"
            mb={3}
            sx={{
              color: COLORS.black,
              fontWeight: 600,
              fontSize: {xs: '1rem', sm: '1.05rem', md: '1.1rem'},
              lineHeight: 1.5,
            }}
          >
            {AI_CALL_INSPECTION_PROMPT_BODY}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              size="medium"
              onClick={aiCallInspectionPrompt.onClose}
              sx={{
                minWidth: 100,
                fontSize: '0.95rem',
                fontWeight: 600,
                color: COLORS.globalBlue,
                borderColor: COLORS.globalBlue,
                '&:hover': {
                  borderColor: COLORS.globalBlueHover,
                  backgroundColor: COLORS.globalBluePale,
                },
              }}
            >
            Close
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={aiCallInspectionPrompt.onOpen}
              sx={{
                minWidth: 100,
                fontSize: '0.95rem',
                fontWeight: 600,
                backgroundColor: COLORS.globalBlue,
                '&:hover': {
                  backgroundColor: COLORS.globalBlueHover,
                },
              }}
            >
            Open
            </Button>
          </Box>
        </Box>
      </ConfirmModal>
      <AiCallIncomingPrompt prompt={aiCallIncomingPrompt} />
      <AiCallInspectionSearchDialog
        open={aiCallInspectionSearch.open}
        call={aiCallInspectionSearch.call}
        onClose={aiCallInspectionSearch.onClose}
      />
    </>
  );
};

export default UserSocketRegistration;
